import { RefObject, useEffect, useRef } from 'react';
import {
  applyHighlightToSelection,
  removeHighlightGroup,
  resolveHighlightElement,
} from '@/components/ora/Study/Exam/components/Highlighting/highlightDom';

// Same settle time QBank gives a touch selection before reading it.
const TOUCH_SELECTION_SETTLE_MS = 300;

const isTouchDevice = () =>
  'ontouchstart' in window || navigator.maxTouchPoints > 0;

const hasTextSelection = () => {
  const selection = window.getSelection();
  return Boolean(
    selection && !selection.isCollapsed && selection.toString().length > 0
  );
};

/**
 * Question-stem highlighting for the in-chat question block (ORA-820): the
 * QBank gestures — select text to highlight it, click a highlight to remove
 * it — bound to one stem instead of the whole document, so several questions
 * in a thread never answer each other's events.
 *
 * The stem's markup is changed in place (the same rangy class applier QBank
 * uses) and `onChange` receives the container's new `innerHTML`; the caller
 * decides what to persist. While `disabled`, no listener is bound and the
 * marks already on the stem stay as they are.
 *
 * `scopeKey` names the question the container shows; a new key rebinds.
 */
export const useScopedStemHighlighting = (
  containerRef: RefObject<HTMLElement>,
  {
    disabled,
    onChange,
    scopeKey,
  }: {
    disabled: boolean;
    onChange: (html: string) => void;
    scopeKey: string;
  }
): void => {
  // The listeners are bound once per question; they read the latest callback.
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || disabled) return;

    let touchSelectionTimeout: number | null = null;
    const reportChange = () => onChangeRef.current(container.innerHTML);

    const handleMouseUp = (event: MouseEvent) => {
      if (applyHighlightToSelection(container)) {
        reportChange();
        return;
      }

      // A click (not the end of a drag-select) on a highlight removes it.
      const highlight = resolveHighlightElement(container, event.target);
      if (!highlight || hasTextSelection()) return;

      removeHighlightGroup(container, highlight);
      reportChange();
    };

    const handleTouchEnd = (event: TouchEvent) => {
      const highlight = resolveHighlightElement(container, event.target);

      if (highlight) {
        removeHighlightGroup(container, highlight);
        reportChange();
        return;
      }

      // Let the native touch selection finish before reading it.
      if (touchSelectionTimeout !== null) {
        window.clearTimeout(touchSelectionTimeout);
      }

      touchSelectionTimeout = window.setTimeout(() => {
        touchSelectionTimeout = null;
        if (applyHighlightToSelection(container)) reportChange();
      }, TOUCH_SELECTION_SETTLE_MS);
    };

    const listensToTouch = isTouchDevice();

    container.addEventListener('mouseup', handleMouseUp);
    if (listensToTouch) container.addEventListener('touchend', handleTouchEnd);

    return () => {
      if (touchSelectionTimeout !== null) {
        window.clearTimeout(touchSelectionTimeout);
      }

      container.removeEventListener('mouseup', handleMouseUp);
      if (listensToTouch) {
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [containerRef, disabled, scopeKey]);
};
